import { StateCreator } from "zustand";

import { SUBTITLES_BACKGROUND } from "../../enums";
import { PersistedStateName, saveToStorage } from "../../helpers/persist";
import {
  AudioTrack,
  CurrentSelectedVideoTrack,
  PlayerVideoTrack,
  SelectedAudioTrack,
  SelectedVideoTrack
} from "../../types/common";
import { ScheduledProgram, Subtitles } from "../../types/player-api";
import { PlayerStoreProperties } from "../store";
import { StoreMiddlewares, ValueOrFn } from "../storeTypes";
import { isCallable } from "../storeUtils";

export type SubtitleCueData = {
  text: string;
  line: number | "auto";
  snapToLines: boolean;
};

export type TracksSliceState = {
  audioTracks: AudioTrack[];
  isLiveEdge: boolean;
  isNativeSubtitles: boolean;
  scheduledProgram:
    | Omit<ScheduledProgram, "audioTracks" | "subtitles">
    | undefined;
  selectedAudioTrackId: number | undefined;
  selectedSubtitles: Subtitles | undefined;
  selectedVideoTrack: CurrentSelectedVideoTrack;
  subtitles: Subtitles[];
  subtitlesBackground: SUBTITLES_BACKGROUND;
  subtitlesCue: string;
  subtitlesCues: SubtitleCueData[];
  subtitlesSizeMultiplier: number;
  useVTTPositioning: boolean;
  userPreferredAudioTrack: SelectedAudioTrack | undefined;
  userPreferredSubtitles: Subtitles | undefined;
  userPreferredVideoTrack: SelectedVideoTrack | undefined;
  videoTracks: PlayerVideoTrack[];
};

type TracksSliceActions = {
  setAudioTracks: (audioTracks: AudioTrack[]) => void;
  setIsLiveEdge: (isLiveEdge: boolean) => void;
  setIsNativeSubtitles: (isNativeSubtitles: boolean) => void;
  setScheduledProgram: (
    scheduledProgram: Omit<ScheduledProgram, "audioTracks" | "subtitles">
  ) => void;
  setSelectedAudioTrackId: (selectedAudioTrackId: number) => void;
  setSelectedSubtitles: (selectedSubtitles: Subtitles | undefined) => void;
  setSelectedVideoTrack: (
    selectedVideoTrack: CurrentSelectedVideoTrack
  ) => void;
  setSubtitles: (subtitles: ValueOrFn<Subtitles[]>) => void;
  setSubtitlesBackground: (
    subtitlesBackground: SUBTITLES_BACKGROUND,
    persist: boolean
  ) => void;
  setSubtitlesCue: (subtitlesCue: string) => void;
  setSubtitlesCues: (subtitlesCues: SubtitleCueData[]) => void;
  setSubtitlesSizeMultiplier: (
    subtitlesSizeMultiplier: number,
    persist: boolean
  ) => void;
  setUseVTTPositioning: (useVTTPositioning: boolean) => void;
  setUserPreferredAudioTrack: (
    userPreferredAudioTrack: SelectedAudioTrack | undefined
  ) => void;
  setUserPreferredSubtitles: (
    userPreferredSubtitles: Subtitles | undefined
  ) => void;
  setUserPreferredVideoTrack: (
    userPreferredVideoTrack: SelectedVideoTrack | undefined
  ) => void;
  setVideoTracks: (videoTracks: PlayerVideoTrack[]) => void;
};

export type TracksSlice = TracksSliceState & TracksSliceActions;

export const TRACKS_SLICE_DEFAULTS: TracksSliceState = {
  audioTracks: [],
  isLiveEdge: false,
  isNativeSubtitles: false,
  scheduledProgram: undefined,
  selectedAudioTrackId: undefined,
  selectedSubtitles: undefined,
  selectedVideoTrack: { index: -1, label: "auto" },
  subtitles: [],
  subtitlesBackground: SUBTITLES_BACKGROUND.NONE,
  subtitlesCue: "",
  subtitlesCues: [],
  subtitlesSizeMultiplier: 1,
  useVTTPositioning: false,
  userPreferredAudioTrack: undefined,
  userPreferredSubtitles: undefined,
  userPreferredVideoTrack: undefined,
  videoTracks: []
};

export const tracksSlice: StateCreator<
  PlayerStoreProperties,
  StoreMiddlewares,
  [],
  TracksSlice
> = (set, getState) => ({
  ...TRACKS_SLICE_DEFAULTS,
  setAudioTracks: audioTracks => set({ audioTracks }, false, "setAudioTracks"),
  setIsLiveEdge: isLiveEdge => set({ isLiveEdge }, false, "setIsLiveEdge"),
  setIsNativeSubtitles: isNativeSubtitles =>
    set({ isNativeSubtitles }, false, "setIsNativeSubtitles"),
  setScheduledProgram: scheduledProgram =>
    set({ scheduledProgram }, false, "setScheduledProgram"),
  setSelectedAudioTrackId: selectedAudioTrackId =>
    set({ selectedAudioTrackId }, false, "setSelectedAudioTrackId"),
  setSelectedSubtitles: selectedSubtitles =>
    set({ selectedSubtitles }, false, "setSelectedSubtitles"),
  setSelectedVideoTrack: selectedVideoTrack =>
    set({ selectedVideoTrack }, false, "setSelectedVideoTrack"),
  setSubtitles: subtitles =>
    set(
      state => {
        state.subtitles = isCallable(subtitles)
          ? subtitles(getState().subtitles)
          : subtitles;
      },
      false,
      "setSubtitles"
    ),
  setSubtitlesBackground: (subtitlesBackground, persist = false) =>
    set(
      state => {
        state.subtitlesBackground = isCallable(subtitlesBackground)
          ? subtitlesBackground(state.subtitlesBackground)
          : subtitlesBackground;
        if (persist) {
          saveToStorage(
            PersistedStateName.SUBTITLES_BACKGROUND,
            state.subtitlesBackground
          );
        }
      },
      false,
      "setSubtitlesBackground"
    ),
  setSubtitlesCue: subtitlesCue =>
    set({ subtitlesCue }, false, "setSubtitlesCue"),
  setSubtitlesCues: subtitlesCues =>
    set({ subtitlesCues }, false, "setSubtitlesCues"),
  setSubtitlesSizeMultiplier: (multiplier, persist = false) =>
    set(
      state => {
        state.subtitlesSizeMultiplier = isCallable(multiplier)
          ? multiplier(state.subtitlesSizeMultiplier)
          : multiplier;
        if (persist) {
          saveToStorage(
            PersistedStateName.SUBTITLES_SIZE,
            state.subtitlesSizeMultiplier
          );
        }
      },
      false,
      "setSubtitlesSizeMultiplier"
    ),
  setUseVTTPositioning: useVTTPositioning =>
    set({ useVTTPositioning }, false, "setUseVTTPositioning"),
  setUserPreferredAudioTrack: userPreferredAudioTrack =>
    set(
      state => {
        state.userPreferredAudioTrack = userPreferredAudioTrack;
        saveToStorage(
          PersistedStateName.USER_PREFERED_AUDIO_TRACK,
          userPreferredAudioTrack
        );
      },
      false,
      "setUserPreferredAudioTrack"
    ),
  setUserPreferredSubtitles: userPreferredSubtitles =>
    set(
      state => {
        state.userPreferredSubtitles = userPreferredSubtitles;
        state.subtitlesCue = "";
        state.subtitlesCues = [];
        saveToStorage(
          PersistedStateName.USER_PREFERRED_SUBTITLES,
          userPreferredSubtitles
        );
      },
      false,
      "setUserPreferredSubtitles"
    ),
  setUserPreferredVideoTrack: userPreferredVideoTrack =>
    set(
      state => {
        state.userPreferredVideoTrack = userPreferredVideoTrack;
        saveToStorage(
          PersistedStateName.USER_PREFERRED_VIDEO_TRACK,
          userPreferredVideoTrack
        );
      },
      false,
      "setUserPreferredVideoTrack"
    ),
  setVideoTracks: videoTracks => set({ videoTracks }, false, "setVideoTracks")
});
